<template>
  <div @click="setFocus">
    <div class="reset-button">
      <a
        v-if="wizardFocus && isSubjectSelected"
        @click="reset"
        @keypress.enter="reset"
        tabindex="0"
      ><i class="fa fa-undo"/> Start Over</a>
    </div>
    <p class="selector-heading">I need a learning goal in
      <dropdown ref="dropdown">
        <button
          class="dropdown-toggle btn-lg btn btn-default"
          :class="{ 'text-muted': !isSubjectSelected }"
          data-role="trigger">{{ subjectSelectorText }} <span class="caret"/></button>
        <template slot="dropdown">
          <li
            v-for="subjectOption in subjects"
            :key="subjectOption"
            class="dropdown-item"
            :class="{
              'locked-option': isSubjectLocked(subjectOption),
              locks: areSubjectsLocked
            }">
            <LockedOption v-if="isSubjectLocked(subjectOption)" :label="subjectOption" />
            <a
              v-else
              role="button"
              @click="updateRouteQuery({ field: 'subject', value: subjectOption })">
              {{ subjectOption }}
            </a>
          </li>
        </template>
      </dropdown>
    </p>
    <div v-if="isSubjectSelected">
      <p class="selector-heading">specifically in
        <dropdown ref="dropdown">
          <button
            :class="{ 'text-muted': !isCategorySelected }"
            class="dropdown-toggle btn-lg btn btn-default"
            data-role="trigger">{{ subjectDomainSelectorText }} <span class="caret"/></button>
          <template slot="dropdown">
            <li
              v-for="categoryOption in categories"
              :key="categoryOption"
              class="dropdown-item"
              :class="{
                'locked-option': isSubjectDomainLocked(unlockedSubject, categoryOption),
                locks: areSubjectDomainsLocked(unlockedSubject)
              }">
              <LockedOption
                v-if="isSubjectDomainLocked(unlockedSubject, categoryOption)"
                :label="categoryOption" />
              <a
                v-else
                role="button"
                @click="updateRouteQuery({ field: 'category', value: categoryOption })">
                {{ categoryOption }}
              </a>
            </li>
          </template>
        </dropdown>
      </p>
      <div class="grade-row">
        <p class="selector-heading">for a student in grade
          <dropdown ref="dropdown">
            <button
              :class="{ 'text-muted': !isGradeSelected }"
              class="dropdown-toggle btn-lg btn btn-default"
              data-role="trigger">{{ gradeSelectorText }} <span class="caret"/></button>
            <template slot="dropdown">
              <li
                v-for="gradeOption in grades"
                :key="gradeOption"
                class="dropdown-item"
                :class="{
                  'locked-option': isSubjectGradeLocked(unlockedSubject, gradeOption),
                  locks: areSubjectGradesLocked(unlockedSubject)
                }">
                <LockedOption v-if="isSubjectGradeLocked(unlockedSubject, gradeOption)" :label="gradeOption" />
                <a
                  v-else
                  role="button"
                  @click="updateRouteQuery({ field: 'grade', value: gradeOption })">
                  {{ gradeOption }}
                </a>
              </li>
            </template>
          </dropdown>
        </p>
      </div>
      <div class="grade-row" v-if="unlockedGrade">
        <p class="selector-heading"> who can access grade level content with
          <dropdown ref="dropdown">
            <button class="dropdown-toggle btn-lg btn btn-default" data-role="trigger">{{ instructionalLevel }} <span class="caret"/></button>
            <template slot="dropdown">
              <li
                v-for="level in instructionalLevelOptions"
                :key="level"
                class="dropdown-item">
                <a role="button" @click="updateRouteQuery({ field: 'instructionalLevel', value: level })">{{ level }}</a></li>
            </template>
          </dropdown>
          individualized support
        </p>
      </div>
      <div class="submit-row">
        <button
          v-if="isGradeSelected"
          @click="sendToBrowse()"
          class="btn btn-primary btn-lg">Get resources for this student</button>
      </div>
    </div>
    <modal
      v-model="noGoals"
      :append-to-body="true"
      title="No goals found">
      <p>Sorry, we currently do not have goals that match your criteria. Click the button below to request this content, and try a different search.</p>
      <div slot="footer">
        <button class="btn btn-default" @click="noGoals=false">Close</button>
        <button class="btn btn-warning" @click="requestContent()">Request Content</button>
      </div>
    </modal>
  </div>
</template>

<script>
import { goalWizardLevels, goalWizardLevelToLevelOfSupport } from '@/util/gradelevels'
import { gradeSort, intForGrade } from '@/util/gradesort'
import { mapGetters, mapState } from 'vuex'
import { getGradesWithResultsForFilters } from '@/api/algolia'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js'
import {PremiumLocksMixin} from '@/contentprofile/premium_locks_mixin';
import LockedOption from '@/components/LockedOption'

export default {
  name: 'BrowseGoalsWizardModal',
  mixins: [RedundantRouteMixin, PremiumLocksMixin],
  components: {
    LockedOption
  },
  props: {
    subject: {
      type: String,
      default: ''
    },
    category: {
      type: String,
      default: ''
    },
    grade: {
      type: String,
      default: ''
    },
    instructionalLevel: {
      type: String,
      default: 'less'
    }
  },
  data() {
    return {
      noGoals: false,
      defaultLevelOptions: [
        goalWizardLevels.LESS,
        goalWizardLevels.SOME,
        goalWizardLevels.MORE,
      ]
    }
  },
  computed: {
    ...mapState({ wizardFocus: state => state.browse.wizardFocus }),
    ...mapGetters('contentProfile', ['publishedSubjects', 'visibleStandardSetsForSubject']),
    subjects() {
      return this.publishedSubjects.map(subjectInfo => subjectInfo.subject)
    },
    unlockedSubject() {
      return this.subjectIfUnlocked(this.subject)
    },
    unlockedCategory() {
      return this.subjectCategoryIfUnlocked(this.unlockedSubject, this.category)
    },
    unlockedGrade() {
      return this.subjectGradeIfUnlocked(this.unlockedSubject, this.grade)
    },
    selectedSubjectInfo() {
      return this.publishedSubjects.filter(subjectInfo => subjectInfo.subject === this.unlockedSubject)[0]
    },
    categories() {
      return (this.selectedSubjectInfo && this.selectedSubjectInfo.categories) || []
    },
    grades() {
      return (this.selectedSubjectInfo && this.selectedSubjectInfo.grades) || []
    },
    isSubjectSelected() {
      return this.subjects.indexOf(this.unlockedSubject) > -1
    },
    isGradeSelected() {
      return this.grades.indexOf(this.unlockedGrade) > -1
    },
    isCategorySelected() {
      return this.unlockedCategory !== ''
    },
    instructionalLevelOptions() {
      return this.defaultLevelOptions
    },
    subjectSelectorText() {
      return this.unlockedSubject.length > 0 ? this.unlockedSubject : 'subject'
    },
    subjectDomainSelectorText() {
      return this.unlockedCategory.length > 0 ? this.unlockedCategory : 'domain (optional)'
    },
    gradeSelectorText() {
      return this.unlockedGrade.length > 0 ? this.unlockedGrade : 'grade'
    },
    urlQuery() {
      return {
        subject: this.subject || undefined,
        category: this.category || undefined,
        grade: this.grade || undefined,
        instructionalLevel: this.instructionalLevel || undefined
      }
    }
  },
  methods: {
    reset() {
      this.$router.replace({
        name: 'BrowseGoalsWizardModal'
      })
    },
    setFocus() {
      this.$store.commit('browse/setWizardFocus', true)
    },
    requestContent() {
      this.$sendEvent('Content Requested', {
        source: 'wizard',
        subject: this.unlockedSubject,
        subjectDomains: this.unlockedCategory,
        gradeLevel: this.unlockedGrade,
        instructionalLevel: this.instructionalLevel
      })
      this.noGoals = false
    },
    async sendToBrowse() {
      var standardSets = this.visibleStandardSetsForSubject(this.unlockedSubject, {
        onlyPublished: true
      })
      if (!standardSets.includes('universal')) {
        standardSets.push('universal')
      }
      // don't include grade here because we want to see all grades that have results
      const gradesWithResults = await getGradesWithResultsForFilters({
        subject: this.unlockedSubject,
        subjectDomain: this.unlockedCategory
      }, standardSets)
      const sortedGradesWithResults = gradeSort(gradesWithResults)
      const redirectGrades = computeRedirectGrades(
        this.unlockedGrade,
        sortedGradesWithResults
      )
      let levelOfSupport = goalWizardLevelToLevelOfSupport[this.instructionalLevel]
      if (redirectGrades.length > 0) {
        let query = {
          subject: this.unlockedSubject,
          subjectDomain: this.unlockedCategory,
          grades: redirectGrades,
          levelOfSupport
        }
        this.$router.push({
          name: 'BrowseGoalsPage',
          query
        })
      } else {
        this.noGoals = true
      }
      this.$sendEvent('Submitted Goal Wizard', {
        subject: this.unlockedSubject,
        subject_domain: this.unlockedCategory,
        grades: [this.unlockedGrade],
        instructional_level: this.instructionalLevel
      })
    },
    updateRouteQuery({ field, value }) {
      let update = {}
      update[field] = value
      if (field === 'subject') {
        update.category = undefined
      }
      if (field === 'grade' && !this.instructionalLevel) {
        update.instructionalLevel = goalWizardLevels.LESS
      }
      this.rerouteIfRouteChanged(this.$route, {query: Object.assign({}, this.urlQuery, update), name: 'BrowseGoalsWizardModal'})
    },
    mounted() {
      this.setFocus()
    }
  }
}
// Encapsulates the convoluted logic of figuring out which grade(s) to redirect to browse
// Takes into account what grades have results with the enrolled grade level and instructional level
// Returning an empty array means there are no eligible grades that would return results
export function computeRedirectGrades(
  enrolledGrade,
  gradesWithResults
) {
  let grades = []
  if (gradesWithResults.includes(enrolledGrade)) {
    grades.push(enrolledGrade)
  } else {
    const highestLowerGradeWithResult = gradesWithResults.filter(
      g => intForGrade(g) < intForGrade(enrolledGrade)
    ).pop()
    if (highestLowerGradeWithResult) {
      grades.push(highestLowerGradeWithResult)
    }
  }
  return grades
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
.submit-row {
  text-align: center;
}
.reset-button {
  display: block;
  min-height: 20px;
  margin-bottom: 20px;
  a {
    cursor: pointer;
    color: darken(@link-color, 5%) //for accessibility color contrast
  }
}
.back-button {
  display: block;
  margin-bottom: 20px;
  cursor: pointer;
  color: darken(@link-color, 5%); //for accessibility color contrast
}
.form-control {
  width: 50%;
}
.dropdown-menu {
  margin-top: -5px;
}
.dropdown-item.locked-option {
  line-height: 1;
  width: 100%;
  cursor: not-allowed;
}
.dropdown-item > a {
  &:hover {
    background-color: var(--blue-default);
    color: white;
  }
}
.dropdown-item.locks {
  margin-bottom: 0.25rem;
}
.dropdown-item.locks a, .locked-option-text {
  line-height: 1.5;
  padding-left: 1.75rem;
}
.locked-option {
  position: relative;
}
.selector-heading {
  font-size: 32px;
  line-height: 60px;
  font-style: italic;
}
p.selector-heading button:not(.lock-button) {
  font-size: 28px;
}
.dropdown {
  font-style: normal;
}
div.dropdown > button {
  line-height: 35px;
  font-size: 24px;
}
div.submit-row button {
  margin-top: 30px;
  font-size: 24px;
}
.text-muted {
  color: var(--gray-default);
}
.caret {
  margin-left: 10px;
  width: 10px;
}

/deep/ .locked-option {
  padding: .2rem;
}
</style>
